import { useEffect, useRef, useState } from "react";

const NATIVE_INVOKE_SRC =
  "https://pl29122083.profitablecpmratenetwork.com/8a2b174aac0e4f77419d776101381760/invoke.js";
const NATIVE_CONTAINER_ID = "container-8a2b174aac0e4f77419d776101381760";

const SMARTLINK_URL =
  "https://www.profitablecpmratenetwork.com/cetvw6y6ra?key=203a42087918087e98fc3855d959728b";

const BANNER_728x90 = {
  key: "473c627f2af91157697d71aa40facb39",
  width: 728,
  height: 90,
} as const;
const BANNER_320x50 = {
  key: "644797c8c6fb3ed04c57f20e95b92b3d",
  width: 320,
  height: 50,
} as const;
const BANNER_300x250 = {
  key: "370c2909b83a837efae9c28f4ed7187c",
  width: 300,
  height: 250,
} as const;

type AtIframeBannerProps = {
  atKey: string;
  width: number;
  height: number;
  loadDelayMs?: number;
  className?: string;
};

function AtIframeBanner({
  atKey,
  width,
  height,
  loadDelayMs = 0,
  className = "",
}: AtIframeBannerProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const timer = window.setTimeout(() => {
      const w = window as Window & {
        atOptions?: Record<string, unknown>;
      };
      w.atOptions = {
        key: atKey,
        format: "iframe",
        height,
        width,
        params: {},
      };
      const script = document.createElement("script");
      script.src = `https://www.highperformanceformat.com/${atKey}/invoke.js`;
      script.async = true;
      el.appendChild(script);
    }, loadDelayMs);

    return () => {
      window.clearTimeout(timer);
      el.replaceChildren();
    };
  }, [atKey, height, width, loadDelayMs]);

  return (
    <div
      ref={containerRef}
      className={`flex items-center justify-center overflow-hidden ${className}`}
      style={{ minHeight: height }}
    />
  );
}

/** Leaderboard (desktop) or mobile banner — only one mounts to avoid atOptions races and double load. */
export function AdsterraTopBanner() {
  const [wide, setWide] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 1024px)").matches
      : false
  );

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = () => setWide(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const spec = wide ? BANNER_728x90 : BANNER_320x50;
  return (
    <div className="flex justify-center py-3" aria-hidden="true">
      <AtIframeBanner
        atKey={spec.key}
        width={spec.width}
        height={spec.height}
        loadDelayMs={0}
        className="max-w-full"
      />
    </div>
  );
}

export function AdsterraNativeBanner() {
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const script = document.createElement("script");
    script.async = true;
    script.setAttribute("data-cfasync", "false");
    script.src = NATIVE_INVOKE_SRC;
    wrap.appendChild(script);
    return () => {
      script.remove();
    };
  }, []);

  return (
    <div
      ref={wrapRef}
      className="rounded-2xl border border-gray-100 bg-white/80 px-4 py-4 shadow-sm"
      aria-hidden="true"
    >
      <div id={NATIVE_CONTAINER_ID} className="w-full" />
    </div>
  );
}

/** Stagger after top banner so window.atOptions is not overwritten mid-invoke. */
export function AdsterraMpuBanner() {
  return (
    <div className="flex justify-center py-3" aria-hidden="true">
      <AtIframeBanner
        atKey={BANNER_300x250.key}
        width={BANNER_300x250.width}
        height={BANNER_300x250.height}
        loadDelayMs={450}
        className="max-w-full"
      />
    </div>
  );
}

type AdsterraFooterSmartlinkProps = {
  label: string;
};

export function AdsterraFooterSmartlink({ label }: AdsterraFooterSmartlinkProps) {
  return (
    <p className="text-center text-xs text-gray-400 md:text-left">
      <a
        href={SMARTLINK_URL}
        target="_blank"
        rel="noopener noreferrer sponsored nofollow"
        className="underline decoration-gray-300 underline-offset-2 hover:text-pink-500"
      >
        {label}
      </a>
    </p>
  );
}
